/** Compact JWS JWT decode — browser-only, no signature verification. */

export const MAX_JWT_CHARS = 32_768;

export type JwtStatus =
	| 'malformed'
	| 'not-supported'
	| 'decoded'
	| 'expired';

export type ClaimHelperRow = {
	key: string;
	raw: unknown;
	display: string;
	note?: string;
};

export type JwtDecodeOk = {
	ok: true;
	status: 'decoded' | 'expired';
	header: Record<string, unknown>;
	payload: Record<string, unknown>;
	headerJson: string;
	payloadJson: string;
	claims: ClaimHelperRow[];
	warnings: string[];
	algDisclosure?: string;
};

export type JwtDecodeErr = {
	ok: false;
	status: 'malformed' | 'not-supported';
	message: string;
	warnings: string[];
};

export type JwtDecodeResult = JwtDecodeOk | JwtDecodeErr;

const TIME_CLAIM_KEYS = new Set(['iat', 'nbf', 'exp']);
const STANDARD_CLAIM_ORDER = ['iss', 'sub', 'aud', 'iat', 'nbf', 'exp'] as const;

function decodeBase64UrlToBytes(segment: string): Uint8Array | null {
	if (!segment || /[^A-Za-z0-9_-]/.test(segment)) {
		return null;
	}
	const padded = segment + '='.repeat((4 - (segment.length % 4)) % 4);
	const b64 = padded.replace(/-/g, '+').replace(/_/g, '/');
	try {
		const binary = atob(b64);
		const out = new Uint8Array(binary.length);
		for (let i = 0; i < binary.length; i += 1) {
			out[i] = binary.charCodeAt(i);
		}
		return out;
	} catch {
		return null;
	}
}

function bytesToUtf8(bytes: Uint8Array): string | null {
	try {
		return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
	} catch {
		return null;
	}
}

function parseJsonObject(text: string): Record<string, unknown> | null {
	try {
		const value = JSON.parse(text) as unknown;
		if (value === null || typeof value !== 'object' || Array.isArray(value)) {
			return null;
		}
		return value as Record<string, unknown>;
	} catch {
		return null;
	}
}

function formatUnixSeconds(value: unknown, nowMs: number): { display: string; note?: string; expired?: boolean } {
	if (typeof value !== 'number' || !Number.isFinite(value)) {
		return { display: String(value), note: 'Not a numeric Unix timestamp' };
	}
	const ms = value * 1000;
	const date = new Date(ms);
	if (Number.isNaN(date.getTime())) {
		return { display: String(value), note: 'Invalid timestamp' };
	}
	const iso = date.toISOString();
	const local = date.toLocaleString(undefined, {
		timeZoneName: 'short',
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
	const display = `${iso} (UTC) · ${local}`;
	if (value * 1000 < nowMs) {
		return { display, note: 'This timestamp is in the past', expired: true };
	}
	return { display, note: 'This timestamp is in the future relative to now' };
}

function buildClaims(payload: Record<string, unknown>, nowMs: number): {
	claims: ClaimHelperRow[];
	expired: boolean;
} {
	const claims: ClaimHelperRow[] = [];
	let expired = false;

	for (const key of STANDARD_CLAIM_ORDER) {
		if (!(key in payload)) {
			continue;
		}
		const raw = payload[key];
		if (TIME_CLAIM_KEYS.has(key)) {
			const formatted = formatUnixSeconds(raw, nowMs);
			if (key === 'exp' && formatted.expired) {
				expired = true;
			}
			claims.push({
				key,
				raw,
				display: formatted.display,
				note: formatted.note,
			});
			continue;
		}
		claims.push({
			key,
			raw,
			display: typeof raw === 'string' ? raw : JSON.stringify(raw),
		});
	}

	return { claims, expired };
}

/**
 * Decode a compact three-segment JWS. Does not verify signatures.
 */
export function decodeJwt(input: string, nowMs: number = Date.now()): JwtDecodeResult {
	const warnings: string[] = [];
	const trimmed = input.trim();

	if (!trimmed) {
		return { ok: false, status: 'malformed', message: 'Paste a JWT to decode.', warnings };
	}

	if (trimmed.length > MAX_JWT_CHARS) {
		return {
			ok: false,
			status: 'malformed',
			message: `Token exceeds the ${MAX_JWT_CHARS.toLocaleString()} character limit.`,
			warnings,
		};
	}

	const parts = trimmed.split('.');
	if (parts.length === 5) {
		return {
			ok: false,
			status: 'not-supported',
			message:
				'This looks like a five-part JWE (encrypted JWT). Only compact three-segment JWS tokens are supported in this tool.',
			warnings,
		};
	}

	if (parts.length !== 3) {
		return {
			ok: false,
			status: 'malformed',
			message: `Expected three base64url segments separated by dots (header.payload.signature). Found ${parts.length} segment(s).`,
			warnings,
		};
	}

	const [headerSeg, payloadSeg] = parts;

	const headerBytes = decodeBase64UrlToBytes(headerSeg);
	if (!headerBytes) {
		return {
			ok: false,
			status: 'malformed',
			message: 'Header segment is not valid base64url.',
			warnings,
		};
	}
	const headerText = bytesToUtf8(headerBytes);
	if (headerText === null) {
		return {
			ok: false,
			status: 'malformed',
			message: 'Header segment is not valid UTF-8 JSON.',
			warnings,
		};
	}
	const header = parseJsonObject(headerText);
	if (!header) {
		return {
			ok: false,
			status: 'malformed',
			message: 'Header segment is not a JSON object.',
			warnings,
		};
	}

	let payload: Record<string, unknown> = {};
	let payloadJson = '{\n}';
	if (payloadSeg === '') {
		warnings.push('Payload segment is empty; treating payload as {}.');
	} else {
		const payloadBytes = decodeBase64UrlToBytes(payloadSeg);
		if (!payloadBytes) {
			return {
				ok: false,
				status: 'malformed',
				message: 'Payload segment is not valid base64url.',
				warnings,
			};
		}
		const payloadText = bytesToUtf8(payloadBytes);
		if (payloadText === null) {
			return {
				ok: false,
				status: 'malformed',
				message: 'Payload segment is not valid UTF-8 JSON.',
				warnings,
			};
		}
		if (payloadText.trim() === '') {
			warnings.push('Payload segment decoded to empty text; treating payload as {}.');
		} else {
			const parsed = parseJsonObject(payloadText);
			if (!parsed) {
				return {
					ok: false,
					status: 'malformed',
					message: 'Payload segment is not a JSON object.',
					warnings,
				};
			}
			payload = parsed;
			payloadJson = JSON.stringify(payload, null, 2);
		}
	}

	const headerJson = JSON.stringify(header, null, 2);
	const alg = typeof header.alg === 'string' ? header.alg : '';
	let algDisclosure: string | undefined;
	if (alg.toLowerCase() === 'none') {
		algDisclosure =
			'Header alg is "none". Decoding still succeeds, but this algorithm means no signature was intended — never treat that as authenticity.';
		warnings.push(algDisclosure);
	}

	const { claims, expired } = buildClaims(payload, nowMs);

	return {
		ok: true,
		status: expired ? 'expired' : 'decoded',
		header,
		payload,
		headerJson,
		payloadJson,
		claims,
		warnings,
		algDisclosure,
	};
}
